/**
 * v39 「콘텐츠」 시트의 판정 (`docs/81` §5.1 · 절대 규칙 106). **순수 함수로 뗀다** — 이 패키지는 node 환경에서
 * 테스트한다(`product-list.ts` 와 같은 이유).
 *
 * 재료는 둘 — 리시버의 재생 목록(`view.items` · 지금부터 이어질 순서)과 카탈로그(`GET …/catalog` · 공개 시리즈).
 * **화면이 다시 정렬하지 않는다** — 1단계의 앞쪽은 재생 목록의 순서, 뒤쪽은 카탈로그의 순서(`Series.order`) 그대로다.
 */

import type {
  CatalogSeries,
  ChannelCatalogResponse,
  LineupItem,
  PlaylistItem,
} from '@apm/contracts';
import { toProductRows } from './product-list.js';
import type { ProductListRow } from './product-list.js';

export type ContentSheetMode = 'flat' | 'series';

/**
 * **고를 것이 하나뿐이면 지금의 시트 그대로다** (D8 · 규칙 106).
 *
 * ```
 * 카탈로그가 없다(아직 · 실패 · 홈쇼핑)                         → 'flat'
 * 재생 목록 밖의 공개 시리즈가 있다                              → 'series'
 * 재생 목록의 공개 시리즈 중 카탈로그의 회차 수가 더 많은 것이 있다 → 'series'  (편성 밖 회차가 라이브러리에 있다)
 * 재생 목록에 공개 시리즈가 둘 이상이다                          → 'series'
 * 그 밖                                                        → 'flat'
 * ```
 *
 * 시리즈 없는 회차(특집 · 경기 녹화)와 비공개 시리즈의 회차는 1단계에서 회차 한 줄이라 판정에 들지 않는다 — 그런 회차만
 * 있는 채널은 지금처럼 평면 목록이다.
 */
export function contentSheetMode(
  items: readonly PlaylistItem[],
  catalog: ChannelCatalogResponse | null,
): ContentSheetMode {
  if (catalog === null) return 'flat';
  const listed = new Map(catalog.series.map((s) => [s.seriesId, s]));
  const inPlaylist = new Map<string, number>();
  for (const item of items) {
    const id = item.content?.seriesId ?? null;
    if (id === null || !listed.has(id)) continue;
    inPlaylist.set(id, (inPlaylist.get(id) ?? 0) + 1);
  }
  if (catalog.series.some((s) => !inPlaylist.has(s.seriesId))) return 'series';
  for (const [id, count] of inPlaylist) {
    if ((listed.get(id)?.episodeCount ?? 0) > count) return 'series';
  }
  return inPlaylist.size >= 2 ? 'series' : 'flat';
}

/** 1단계의 한 줄 */
export type ContentRow =
  | {
      kind: 'series';
      seriesId: string;
      title: string;
      thumbnailUrl: string;
      episodeCount: number;
      /** 지금 재생 중인 회차가 이 시리즈다 */
      current: boolean;
      /** 재생 목록에 이 시리즈의 회차가 있다(「편성 중」) */
      inPlaylist: boolean;
    }
  | { kind: 'item'; row: ProductListRow };

/**
 * 1단계의 줄들 — **재생 목록의 순서대로 묶고(지금 보는 시리즈가 맨 위), 그 밖의 라이브러리는 카탈로그 순서**(D4).
 *
 * 재생 목록의 회차가 공개 시리즈에 속하면 그 시리즈의 줄 하나로 묶이고, 아니면(시리즈 없음 · 비공개 시리즈) 회차 한 줄이다.
 * 재생 목록이 회전돼 있으므로(`selectItem` · 이어보기) 첫 줄이 곧 「지금 보는 것」이다 — 따로 맨 위로 올리지 않는다.
 */
export function toContentRows(
  items: readonly PlaylistItem[],
  currentProductId: string | null,
  catalog: ChannelCatalogResponse,
): ContentRow[] {
  const listed = new Map(catalog.series.map((s) => [s.seriesId, s]));
  const productRows = toProductRows(items, currentProductId);
  const rows: ContentRow[] = [];
  const seen = new Map<string, Extract<ContentRow, { kind: 'series' }>>();

  items.forEach((item, index) => {
    const productRow = productRows[index] as ProductListRow;
    const series = listed.get(item.content?.seriesId ?? '');
    if (series === undefined) {
      rows.push({ kind: 'item', row: productRow });
      return;
    }
    const existing = seen.get(series.seriesId);
    if (existing !== undefined) {
      if (productRow.current) existing.current = true;
      return;
    }
    const row = seriesRow(series, productRow.current, true);
    seen.set(series.seriesId, row);
    rows.push(row);
  });

  for (const series of catalog.series) {
    if (!seen.has(series.seriesId)) rows.push(seriesRow(series, false, false));
  }
  return rows;
}

function seriesRow(
  series: CatalogSeries,
  current: boolean,
  inPlaylist: boolean,
): Extract<ContentRow, { kind: 'series' }> {
  return {
    kind: 'series',
    seriesId: series.seriesId,
    title: series.title,
    thumbnailUrl: series.thumbnailUrl,
    episodeCount: series.episodeCount,
    current,
    inPlaylist,
  };
}

/**
 * 2단계의 줄들 — 카탈로그의 카드(`LineupItem`)를 지금의 회차 행(`ProductListRow`)으로. **시즌 → 회차 순 그대로**다.
 *
 * 카드에는 재생 필드가 없고 행도 그것을 쓰지 않는다 — 고르면 `selectItem(productId)` 가 서버의 `/playlist` 를 다시 받는다
 * (규칙 105).
 */
export function toEpisodeRows(
  episodes: readonly LineupItem[],
  currentProductId: string | null,
): ProductListRow[] {
  return episodes.map((episode) => ({
    productId: episode.productId,
    title: episode.title,
    category: episode.meta.category,
    price: episode.price,
    thumbnailUrl: episode.thumbnailUrl,
    current: currentProductId !== null && episode.productId === currentProductId,
    content: episode.content,
    durationSec: episode.durationSec,
  }));
}

/**
 * 2단계에 들어갈 때 포커스가 앉을 회차 — **이어볼 회차 → 지금 회차 → 첫 회차** (`docs/81` §5.1).
 */
export function episodeEntryFocus(
  rows: readonly ProductListRow[],
  resumeProductId: string | null,
): string | null {
  const resume = rows.find((row) => row.productId === resumeProductId);
  if (resume !== undefined) return resume.productId;
  return (rows.find((row) => row.current) ?? rows[0])?.productId ?? null;
}
